import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { chartTooltipProps } from './chartTooltip';

const INCOME_COLOR = '#16a34a';
const EXPENSE_COLOR = '#dc2626';
const NET_COLOR = '#2563eb';

interface IncomeExpenseChartProps {
  data: Array<{ period: string; income: number; expenses: number; net?: number }>;
  height?: number;
  showNet?: boolean;
}

/**
 * Formats a numeric value as a USD currency string.
 *
 * Args:
 *   value (number): The monetary amount to format.
 *
 * Returns:
 *   string: Formatted currency string.
 */
const formatCurrency = (value: number): string =>
  `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * A grouped bar chart comparing income and expenses with an optional net line.
 *
 * Uses a ComposedChart when showNet is true to overlay a line on bars.
 *
 * Args:
 *   data (Array): Array of period data with income, expenses, and optional net.
 *   height (number): Chart height in pixels (default 400).
 *   showNet (boolean): Whether to show the net line overlay.
 *
 * Returns:
 *   React.ReactElement: The rendered income vs expense chart.
 */
export const IncomeExpenseChart: React.FC<IncomeExpenseChartProps> = ({
  data,
  height = 400,
  showNet = false,
}) => {
  // Reason: SVG attributes don't resolve CSS var(), so grid/tick colours come
  // from the resolved MUI palette instead of tokens.css.
  const theme = useTheme();

  if (data.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No cash flow data for the selected period
      </Typography>
    );
  }

  const tooltipFormatter = (value: number, name: string) => [
    formatCurrency(value),
    name,
  ];

  const chartProps = {
    data,
    margin: { top: 5, right: 20, left: 10, bottom: 5 },
  };

  const commonElements = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
      <XAxis
        dataKey="period"
        tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
        tickLine={false}
      />
      <YAxis
        tickFormatter={(v: number) => `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`}
        tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
        tickLine={false}
        axisLine={false}
      />
      <Tooltip {...chartTooltipProps} formatter={tooltipFormatter} />
      <Legend verticalAlign="bottom" height={36} />
      {/* Reason: animated series render blank or mis-scaled on first mount
          under React 18 StrictMode — same workaround as PositionMix. */}
      <Bar
        dataKey="income"
        name="Income"
        fill={INCOME_COLOR}
        radius={[4, 4, 0, 0]}
        isAnimationActive={false}
      />
      <Bar
        dataKey="expenses"
        name="Expenses"
        fill={EXPENSE_COLOR}
        radius={[4, 4, 0, 0]}
        isAnimationActive={false}
      />
    </>
  );

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        {showNet ? (
          <ComposedChart {...chartProps}>
            {commonElements}
            <Line
              type="monotone"
              dataKey="net"
              name="Net"
              stroke={NET_COLOR}
              strokeWidth={2}
              dot={{ r: 3, fill: NET_COLOR }}
              isAnimationActive={false}
            />
          </ComposedChart>
        ) : (
          <BarChart {...chartProps}>
            {commonElements}
          </BarChart>
        )}
      </ResponsiveContainer>
    </Box>
  );
};
